"use client";

import { type ReactNode, useState } from "react";
import {
  CipherGrid,
  type CipherMode,
  CipherWorkbench,
  type GridTone,
} from "@/components/tool-kit/cipher-workbench";
import {
  Checkbox,
  DataTable,
  Notice,
  type Option,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SMALL,
  CARD,
  HINT,
  LABEL,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { parseIntegerInput } from "@/lib/parse-number";
import {
  applyTextOptions,
  type CipherTextOptions,
} from "@/lib/tool-kit/ciphers/alphabet";
import {
  MAX_COLUMNS,
  MIN_COLUMNS,
  padTokens,
  ROUTE_CORNERS,
  ROUTE_PATTERNS,
  type RouteCorner,
  type RouteOptions,
  type RoutePattern,
  routeCrack,
  routeDecode,
  routeEncode,
  routeGrid,
  routeSteps,
} from "@/lib/tool-kit/ciphers-misc/route";

type RouteUnit = "letters" | "words";

const WORKING_LIMIT = 400;
const STEP_NUMBER_LIMIT = 99;
const CANDIDATES_SHOWN = 8;
const FILLER = "X";
const SPACE_MARK = "␣";
const WHITESPACE = /\s/;
const WORD_SPLIT = /\s+/;
const DEFAULT_OPTIONS: CipherTextOptions = {
  keepCase: true,
  keepSpaces: false,
  keepPunctuation: false,
};

/** English strings. A translated content file overrides these through `ui`. */
export const ROUTE_CIPHER_STRINGS = {
  mode: "Mode",
  encode: "Encode",
  decode: "Decode",
  plainText: "Plain text",
  cipherText: "Cipher text",
  decodedText: "Decoded text",
  inputPlaceholder: "Type or paste your message",
  sample: "We are discovered flee at once",
  sampleWords: "The general will arrive at dawn with three hundred men",
  columns: "Grid width (columns)",
  columnsHint:
    "The message is written into rows this wide ({min}–{max}). Width, route and corner are the whole key.",
  unit: "What moves",
  unitLetters: "Letters",
  unitWords: "Whole words",
  pattern: "Route",
  patternSpiralCw: "Spiral inward, clockwise",
  patternSpiralCcw: "Spiral inward, counter-clockwise",
  patternColumns: "Column by column",
  patternColumnsSnake: "Columns, zigzag",
  patternRowsSnake: "Rows, zigzag",
  corner: "Start in the",
  cornerTopLeft: "Top left",
  cornerTopRight: "Top right",
  cornerBottomLeft: "Bottom left",
  cornerBottomRight: "Bottom right",
  reverse: "Read the route backwards (last cell first)",
  fill: "Fill the empty cells at the end with X",
  fillHint:
    "A short last row is simply skipped by the route. Filling makes the grid a full rectangle; the X stays in the decoded text.",
  keep: "Keep",
  keepCase: "Upper and lower case",
  keepSpaces: "Spaces",
  keepPunctuation: "Punctuation and numbers",
  keepHint:
    "Whatever you keep is written into the grid like a letter; anything else is removed before the message is laid out. Whole words move as they are.",
  copy: "Copy result",
  copied: "Copied!",
  clear: "Clear",
  swap: "Swap input and output",
  working: "The grid and the route",
  gridLabel: "Message grid",
  stepsLabel: "Reading order",
  columnLabel: "Column {n}",
  rowLabel: "Row {n}",
  gridIntro:
    "The message is written into the grid row by row. The route then visits the cells in the order numbered below; the first cell is blue and the last is amber.",
  gridSize:
    "one: {rows} row of {columns} columns.|other: {rows} rows of {columns} columns.",
  stepsIntro:
    "The number in each cell is the step at which the route reads it.",
  stepsSkipped:
    "The step numbers are shown for messages up to {limit} characters.",
  wordCell: "{word} ({step})",
  tooBig: "The grid is shown for messages up to {limit} characters or words.",
  emptyWorking: "Type a message to see the grid.",
  tooShort: "A message needs at least two letters or words to be routed.",
  crackToggle: "Try every grid width",
  crackHeading: "Grid widths, most English-like first",
  crackHint:
    "Each line decodes the cipher text with a different grid width, using the route and corner chosen above, and ranks the results by common English letter pairs. On a short message the order is a hint, not a verdict.",
  crackUse: "Use {columns}",
  crackColumns: "one: {columns} column|other: {columns} columns",
} as const;

type Strings = typeof ROUTE_CIPHER_STRINGS;

function visible(char: string | null): string | null {
  if (char === null) {
    return null;
  }
  return WHITESPACE.test(char) ? SPACE_MARK : char;
}

function stepTone(
  steps: readonly (number | null)[],
  total: number
): (index: number) => GridTone | undefined {
  return (index) => {
    if (steps[index] === 1) {
      return "primary";
    }
    return steps[index] === total ? "secondary" : undefined;
  };
}

function patternOptions(strings: Strings): Option<RoutePattern>[] {
  const labels: Record<RoutePattern, string> = {
    "spiral-cw": strings.patternSpiralCw,
    "spiral-ccw": strings.patternSpiralCcw,
    columns: strings.patternColumns,
    "columns-snake": strings.patternColumnsSnake,
    "rows-snake": strings.patternRowsSnake,
  };
  return ROUTE_PATTERNS.map((value) => ({ value, label: labels[value] }));
}

function cornerOptions(strings: Strings): Option<RouteCorner>[] {
  const labels: Record<RouteCorner, string> = {
    "top-left": strings.cornerTopLeft,
    "top-right": strings.cornerTopRight,
    "bottom-left": strings.cornerBottomLeft,
    "bottom-right": strings.cornerBottomRight,
  };
  return ROUTE_CORNERS.map((value) => ({ value, label: labels[value] }));
}

interface WorkingProps {
  strings: Strings;
  tokens: string[];
  options: RouteOptions;
  unit: RouteUnit;
}

function RouteWorking({ strings, tokens, options, unit }: WorkingProps) {
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const grid = routeGrid(tokens, options.columns);
  const steps = routeSteps(tokens.length, options);
  const flatSteps = steps.flat();
  const columnHeaders = grid[0].map((_, index) =>
    t(strings.columnLabel, { n: fmt(index + 1) })
  );
  const rowHeaders = grid.map((_, index) =>
    t(strings.rowLabel, { n: fmt(index + 1) })
  );
  const intro = (
    <>
      <p className="text-muted-foreground text-sm leading-relaxed">
        {strings.gridIntro}
      </p>
      <p className={LABEL}>
        {tPlural(strings.gridSize, locale, grid.length, {
          rows: fmt(grid.length),
          columns: fmt(options.columns),
        })}
      </p>
    </>
  );

  if (unit === "words") {
    const rows = grid.map((row, rowIndex) =>
      row.map((word, columnIndex) => {
        const step = steps[rowIndex][columnIndex];
        return word === null || step === null
          ? ""
          : t(strings.wordCell, { word, step: fmt(step) });
      })
    );
    return (
      <>
        {intro}
        <DataTable head={columnHeaders} rows={rows} />
      </>
    );
  }

  const showSteps = tokens.length <= STEP_NUMBER_LIMIT;
  return (
    <>
      {intro}
      <CipherGrid
        cells={grid.flat().map(visible)}
        columnHeaders={columnHeaders}
        columns={options.columns}
        dense
        label={strings.gridLabel}
        rowHeaders={rowHeaders}
        tone={stepTone(flatSteps, tokens.length)}
      />
      {showSteps ? (
        <div className="space-y-2">
          <p className={LABEL}>{strings.stepsLabel}</p>
          <p className={HINT}>{strings.stepsIntro}</p>
          <CipherGrid
            cells={flatSteps.map((step) => (step === null ? null : fmt(step)))}
            columnHeaders={columnHeaders}
            columns={options.columns}
            dense
            label={strings.stepsLabel}
            rowHeaders={rowHeaders}
            tone={stepTone(flatSteps, tokens.length)}
          />
        </div>
      ) : (
        <p className={HINT}>
          {t(strings.stepsSkipped, { limit: fmt(STEP_NUMBER_LIMIT) })}
        </p>
      )}
    </>
  );
}

export default function RouteCipherWidget() {
  const strings = useToolUi(ROUTE_CIPHER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [mode, setMode] = useState<CipherMode>("encode");
  const [input, setInput] = useState<string>(strings.sample);
  const [columnsText, setColumnsText] = useState("5");
  const [pattern, setPattern] = useState<RoutePattern>("spiral-cw");
  const [corner, setCorner] = useState<RouteCorner>("top-left");
  const [unit, setUnit] = useState<RouteUnit>("letters");
  const [reverse, setReverse] = useState(false);
  const [fill, setFill] = useState(false);
  const [textOptions, setTextOptions] = useState(DEFAULT_OPTIONS);
  const [crack, setCrack] = useState(false);

  const columns = Math.min(
    MAX_COLUMNS,
    Math.max(MIN_COLUMNS, parseIntegerInput(columnsText) ?? MIN_COLUMNS)
  );
  const options: RouteOptions = { columns, pattern, corner, reverse };
  const joiner = unit === "words" ? " " : "";
  const rawTokens =
    unit === "words"
      ? input.split(WORD_SPLIT).filter((word) => word.length > 0)
      : [...applyTextOptions(input, textOptions)];
  const tokens =
    mode === "encode" && fill
      ? padTokens(rawTokens, columns, FILLER)
      : rawTokens;
  const result =
    mode === "encode"
      ? routeEncode(tokens, options)
      : routeDecode(tokens, options);
  const output = result.join(joiner);
  const plainTokens = mode === "encode" ? tokens : result;

  function changeUnit(next: RouteUnit) {
    if (input === strings.sample || input === strings.sampleWords) {
      setInput(next === "words" ? strings.sampleWords : strings.sample);
    }
    setUnit(next);
  }

  const keyControls = (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          hint={t(strings.columnsHint, {
            min: fmt(MIN_COLUMNS),
            max: fmt(MAX_COLUMNS),
          })}
          id="rt-columns"
          label={strings.columns}
          numeric
          onChange={setColumnsText}
          value={columnsText}
        />
        <SelectField
          id="rt-unit"
          label={strings.unit}
          onChange={changeUnit}
          options={[
            { value: "letters", label: strings.unitLetters },
            { value: "words", label: strings.unitWords },
          ]}
          value={unit}
        />
        <SelectField
          id="rt-pattern"
          label={strings.pattern}
          onChange={setPattern}
          options={patternOptions(strings)}
          value={pattern}
        />
        <SelectField
          id="rt-corner"
          label={strings.corner}
          onChange={setCorner}
          options={cornerOptions(strings)}
          value={corner}
        />
      </div>
      <div className="flex flex-wrap gap-x-6 gap-y-2">
        <Checkbox
          checked={reverse}
          label={strings.reverse}
          onChange={setReverse}
        />
        {mode === "encode" ? (
          <Checkbox checked={fill} label={strings.fill} onChange={setFill} />
        ) : (
          <Checkbox
            checked={crack}
            label={strings.crackToggle}
            onChange={setCrack}
          />
        )}
      </div>
      {mode === "encode" ? <p className={HINT}>{strings.fillHint}</p> : null}
    </div>
  );

  const candidates =
    mode === "decode" && crack && unit === "letters" && tokens.length > 2
      ? routeCrack(tokens, { pattern, corner, reverse }).slice(
          0,
          CANDIDATES_SHOWN
        )
      : [];

  const extras =
    candidates.length > 0 ? (
      <section className={`${CARD} space-y-3`}>
        <h2 className={WIDGET_H2}>{strings.crackHeading}</h2>
        <p className={HINT}>{strings.crackHint}</p>
        <ul className="divide-y divide-border">
          {candidates.map((candidate) => (
            <li
              className="flex flex-wrap items-center gap-3 py-2"
              key={candidate.columns}
            >
              <span className="w-24 shrink-0 text-muted-foreground text-xs">
                {tPlural(strings.crackColumns, locale, candidate.columns, {
                  columns: fmt(candidate.columns),
                })}
              </span>
              <span className="min-w-0 flex-1 break-all font-mono text-foreground text-sm">
                {candidate.plain.join("")}
              </span>
              <button
                className={BUTTON_SMALL}
                onClick={() => setColumnsText(String(candidate.columns))}
                type="button"
              >
                {t(strings.crackUse, { columns: fmt(candidate.columns) })}
              </button>
            </li>
          ))}
        </ul>
      </section>
    ) : null;

  let working: ReactNode;
  if (plainTokens.length < MIN_COLUMNS) {
    working = (
      <Notice>
        {input.length === 0 ? strings.emptyWorking : strings.tooShort}
      </Notice>
    );
  } else if (plainTokens.length > WORKING_LIMIT) {
    working = (
      <Notice>{t(strings.tooBig, { limit: fmt(WORKING_LIMIT) })}</Notice>
    );
  } else {
    working = (
      <RouteWorking
        options={options}
        strings={strings}
        tokens={plainTokens}
        unit={unit}
      />
    );
  }

  return (
    <CipherWorkbench
      extras={extras}
      idPrefix="rt"
      input={input}
      keyControls={keyControls}
      labels={{
        mode: strings.mode,
        encode: strings.encode,
        decode: strings.decode,
        input: mode === "encode" ? strings.plainText : strings.cipherText,
        inputPlaceholder: strings.inputPlaceholder,
        output: mode === "encode" ? strings.cipherText : strings.decodedText,
        textOptions: strings.keep,
        keepCase: strings.keepCase,
        keepSpaces: strings.keepSpaces,
        keepPunctuation: strings.keepPunctuation,
        copy: strings.copy,
        copied: strings.copied,
        clear: strings.clear,
        swap: strings.swap,
        working: strings.working,
      }}
      mode={mode}
      notices={<p className={HINT}>{strings.keepHint}</p>}
      onInputChange={setInput}
      onModeChange={setMode}
      output={output}
      textOptions={
        unit === "letters"
          ? {
              value: textOptions,
              onChange: setTextOptions,
              show: ["keepCase", "keepSpaces", "keepPunctuation"],
            }
          : undefined
      }
      working={working}
    />
  );
}
